import React, { useState } from 'react';
import type { QuizQuestion as QuizQuestionType } from '../../types/course';
import { playCorrectSound, playIncorrectSound } from '../../utils/sound';

interface Props {
  question: QuizQuestionType;
  onAnswered: (isCorrect: boolean) => void;
}

export const QuizQuestion: React.FC<Props> = ({ question, onAnswered }) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);

  const handleSelect = (index: number) => {
    if (selectedIndex !== null) return; // Already answered
    setSelectedIndex(index);
    setShowExplanation(true);

    const isCorrect = index === question.correctIndex;
    if (isCorrect) {
      playCorrectSound();
    } else {
      playIncorrectSound();
    }
  };

  const handleContinue = () => {
    if (selectedIndex === null) return;
    onAnswered(selectedIndex === question.correctIndex);
  };

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-base font-semibold text-[var(--clue-text-primary)] leading-snug">
        {question.question}
      </h3>

      <div className="space-y-2">
        {question.options.map((option, i) => {
          let optionStyle = 'border-[var(--clue-border-mid)] bg-[var(--clue-bg-primary)] hover:bg-[var(--clue-bg-secondary)]';

          if (selectedIndex !== null) {
            if (i === question.correctIndex) {
              optionStyle = 'border-[var(--clue-green-accent)] bg-[var(--clue-green-accent)]/10 ring-1 ring-[var(--clue-green-accent)]';
            } else if (i === selectedIndex) {
              optionStyle = 'border-red-400 bg-red-500/10 ring-1 ring-red-400';
            } else {
              optionStyle = 'border-[var(--clue-border-mid)] bg-[var(--clue-bg-primary)] opacity-50';
            }
          }

          return (
            <button
              key={i}
              onClick={() => handleSelect(i)}
              disabled={selectedIndex !== null}
              className={`w-full text-left px-4 py-3 rounded-lg border text-sm transition-all duration-200 ${optionStyle}`}
            >
              <span className="text-[var(--clue-text-primary)]">{option}</span>
            </button>
          );
        })}
      </div>

      {/* Explanation card */}
      {showExplanation && (
        <div
          className={`rounded-lg p-4 border ${
            selectedIndex === question.correctIndex
              ? 'bg-[var(--clue-green-accent)]/10 border-[var(--clue-green-accent)]/30'
              : 'bg-red-500/10 border-red-400/30'
          }`}
        >
          <p className="text-xs font-semibold mb-1 text-[var(--clue-text-primary)]">
            {selectedIndex === question.correctIndex ? 'Correct!' : 'Not quite!'}
          </p>
          <p className="text-sm text-[var(--clue-text-secondary)] leading-relaxed">
            {question.explanation}
          </p>
        </div>
      )}

      {showExplanation && (
        <button
          onClick={handleContinue}
          className="w-full h-11 rounded-lg bg-[var(--clue-green-accent)] text-white text-sm font-semibold hover:opacity-90 transition-opacity"
        >
          Continue
        </button>
      )}
    </div>
  );
};
